/*=========================================================================================
  File Name: captionedVideo.js
  Description: Shared caption / transcript behaviour for the three self-hosted video
               players (VideoPopup, VideoFilePopup, ResponsiveIframe).

               The component supplies:
                 videoSourceUrl      computed - the MP4 URL being played
                 explicitCaptions    computed - a caption URL passed in by the caller, or ''
                 explicitTranscript  computed - transcript text/HTML passed in, or ''

               and gets back:
                 captionsUrl         the <track src> to render, or '' when none is available
                 transcriptText      a plain-text transcript built from the loaded cues
                 onCaptionsLoad / onCaptionsError  handlers for the <track>

               Why the caption file is fetched here rather than put straight into
               <track src>: a cross-origin <track> only loads when the <video> carries
               `crossorigin`, and a crossorigin <video> only plays when the CDN answers the
               MP4 request with Access-Control-Allow-Origin - which CloudFront caches
               inconsistently, so videos would randomly fail to play. Fetching the .vtt
               ourselves and handing the browser a same-origin blob: URL means the <video>
               never needs `crossorigin`; only the small .vtt request needs CORS, and if it
               fails the video simply plays without captions.
==========================================================================================*/

import { captionsUrlFor, transcriptFromCues } from '@/utils/captions';

export default {
  data() {
    return {
      captionsUrl: '',
      cueTranscript: '',
    };
  },

  computed: {
    transcriptText() {
      return this.explicitTranscript ? '' : this.cueTranscript;
    },
  },

  watch: {
    videoSourceUrl: {
      immediate: true,
      handler() {
        this.loadCaptions();
      },
    },
  },

  beforeDestroy() {
    this.releaseCaptions();
  },

  methods: {
    async loadCaptions() {
      this.releaseCaptions();
      this.cueTranscript = '';

      const url = this.explicitCaptions || captionsUrlFor(this.videoSourceUrl);
      if (!url) return;

      const requested = this.videoSourceUrl;
      try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) return;
        const text = await response.text();
        // The video changed while the fetch was in flight.
        if (requested !== this.videoSourceUrl || !/^WEBVTT/.test(text)) return;
        this.captionsUrl = URL.createObjectURL(new Blob([text], { type: 'text/vtt' }));
      } catch (e) {
        // No caption file, or the CDN refused the cross-origin request: play without.
      }
    },

    releaseCaptions() {
      if (this.captionsUrl) URL.revokeObjectURL(this.captionsUrl);
      this.captionsUrl = '';
    },

    onCaptionsLoad(event) {
      const track = event.target && event.target.track;
      if (track && track.cues) this.cueTranscript = transcriptFromCues(track.cues);
    },

    onCaptionsError() {
      this.releaseCaptions();
    },
  },
};
